/**
 * Times on a phone (mobile redesign): the focus car's laps as a table, with the lap-time chart one
 * tap away (Table | Chart, remembered on this device), and Sectors when the race's timing has them
 * (SectorsView).
 *
 *   Best 1:31.540 · Avg 1:45.218                [Table | Chart]
 *   Lap    Lap time     Race avg
 *   249    1:33.978     1:45.218
 *   248    1:32.101     1:45.264
 *   210    1:31.540  ← the car's best, in green
 *   …      PIT / FCY laps marked
 *
 * Race avg: the car's average lap over the race up to and including that lap (its race time
 * divided by its laps). A live race lists the newest lap first; a finished race lap 1 first.
 * Tapping a lap picks it (the race page's selected lap, shared with the charts).
 */
import { useMemo, useState, type ReactNode } from "react";
import type { RaceChartData } from "@shared/types";
import { TraceControls } from "./TraceControls";
import { PC } from "./phone-cards";
import type { TraceView } from "./trace-view";
import { CollapsingBars, useChromeHidden } from "../../lib/hideOnScroll";
import { isGreenFlag } from "./flags";

const GREEN = "#4ade80";
const CAUTION = "#fab219";
const PIT = "#2563eb";
const VIEW_KEY = "rt.timesView";

/** Seconds as M:SS.mmm. */
export function lapClock(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, "0")}`;
}

export interface LapRow {
  lap: number;
  time: number;
  /** The car's average lap up to and including this one. */
  avg: number;
  pit: boolean;
  caution: boolean;
  best: boolean;
}

/** A car's laps with the race average at each, and its best lap marked. Laps without a time are left out. */
export function lapRows(data: RaceChartData, focusNum: number): LapRow[] {
  const laps = (data.cars[String(focusNum)]?.laps ?? []).filter((l) => l.ltSec > 0).sort((a, b) => a.l - b.l);
  const best = laps.reduce((m, l) => Math.min(m, l.ltSec), Infinity);
  let sum = 0;
  return laps.map((l, i) => {
    sum += l.ltSec;
    return { lap: l.l, time: l.ltSec, avg: sum / (i + 1), pit: l.pit === 1, caution: !isGreenFlag(l.flag), best: l.ltSec === best };
  });
}

type TimesView = "table" | "chart" | "sectors";
function readView(): TimesView {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "chart" || v === "sectors" ? v : "table";
  } catch {
    return "table";
  }
}

export function PhoneLapTimes({
  data,
  focusNum,
  setFocusNum,
  compSet,
  classView,
  view,
  activeLap,
  setActiveLap,
  live = false,
  phoneSlot = null,
  chart,
  sectors = null,
}: {
  data: RaceChartData;
  focusNum: number;
  setFocusNum: (n: number) => void;
  compSet: Set<number>;
  classView: string;
  view: TraceView;
  activeLap: number | null;
  setActiveLap: (l: number) => void;
  live?: boolean;
  phoneSlot?: HTMLElement | null;
  /** The lap-time chart, shown for Chart. */
  chart: ReactNode;
  /** The Sectors view, when the race's timing has sector times (a third tab). */
  sectors?: ReactNode;
}) {
  const [picked, setMode] = useState<TimesView>(readView);
  // Sectors remembered from another race shows the table on a race without them.
  const mode: TimesView = picked === "sectors" && !sectors ? "table" : picked;
  const pick = (m: TimesView) => {
    setMode(m);
    try {
      localStorage.setItem(VIEW_KEY, m);
    } catch {
      /* private window: for this visit only */
    }
  };
  const chromeHidden = useChromeHidden();
  const rows = useMemo(() => lapRows(data, focusNum), [data, focusNum]);
  const shown = live ? [...rows].reverse() : rows;
  const best = rows.find((r) => r.best);
  const last = rows[rows.length - 1];

  const toggle = (
    <div className="flex h-11 shrink-0 items-center justify-between gap-2 px-3" data-phone-times="toggle">
      <span className="min-w-0 truncate text-[13px]" style={{ color: PC.muted }}>
        {mode === "table" && best ? (
          <>
            Best <span className="font-mono font-semibold" style={{ color: GREEN }}>{lapClock(best.time)}</span>
            {last ? (
              <>
                {" · "}Avg <span className="font-mono" style={{ color: PC.text }}>{lapClock(last.avg)}</span>
              </>
            ) : null}
          </>
        ) : mode === "table" ? (
          "No lap times yet"
        ) : mode === "sectors" ? (
          "Sectors"
        ) : (
          "Lap time chart"
        )}
      </span>
      <div role="tablist" aria-label="Times view" className="flex shrink-0 rounded-full border p-0.5" style={{ borderColor: PC.border }}>
        {(sectors ? (["table", "chart", "sectors"] as const) : (["table", "chart"] as const)).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            title={
              m === "table"
                ? "The focus car's laps as a table"
                : m === "chart"
                  ? "Every car's lap-time rank, lap by lap, as a chart"
                  : "Best and median sectors, theoretical best and spread, over green racing laps"
            }
            onClick={() => pick(m)}
            data-touch-44
            className="relative h-8 rounded-full px-3 text-[13px] font-semibold before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']"
            style={mode === m ? { background: PC.accent, color: "#0b0b18" } : { color: PC.muted }}
          >
            {m === "table" ? "Table" : m === "chart" ? "Chart" : "Sectors"}
          </button>
        ))}
      </div>
    </div>
  );

  if (mode === "chart" || mode === "sectors") {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {toggle}
        <div className={`flex min-h-0 flex-1 flex-col${mode === "sectors" ? " overflow-y-auto" : ""}`}>{mode === "chart" ? chart : sectors}</div>
      </div>
    );
  }

  const tag = (text: string, bg: string, ink = "#fff") => (
    <span className="rounded px-1 text-[10px] font-bold" style={{ background: bg, color: ink }}>
      {text}
    </span>
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col" style={{ background: PC.bg, color: PC.text }}>
      <CollapsingBars hidden={chromeHidden}>
        <TraceControls data={data} focusNum={focusNum} onFocus={setFocusNum} classView={classView} compSet={compSet} view={view} phoneSlot={phoneSlot} />
        {toggle}
      </CollapsingBars>
      <div className="custom-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-6" data-phone-times="table">
        <table className="mx-auto w-full max-w-xl border-collapse text-[15px]">
          <thead className="sticky top-0 z-10" style={{ background: PC.bg }}>
            <tr className="text-[12px] font-semibold uppercase tracking-[0.06em]" style={{ color: PC.muted }}>
              <th title="The lap number; PIT marks a lap that ended in the pits, FCY one run under caution" className="py-2 pl-1 text-left font-semibold">Lap</th>
              <th title="The lap's time; the car's best lap is green" className="py-2 text-right font-semibold">Lap time</th>
              <th title="The car's average lap up to and including this one: its race time over its laps, pit and caution laps included" className="py-2 pr-1 text-right font-semibold">Race avg</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const on = r.lap === activeLap;
              return (
                <tr
                  key={r.lap}
                  onClick={() => setActiveLap(r.lap)}
                  aria-selected={on}
                  data-best={r.best || undefined}
                  className="cursor-pointer border-t"
                  style={{ borderColor: PC.line, background: on ? "rgba(244,140,52,0.14)" : r.best ? "rgba(74,222,128,0.08)" : undefined }}
                >
                  <td className="py-2.5 pl-1">
                    <span className="flex items-center gap-1.5">
                      <span className="w-9 font-mono" style={{ color: PC.soft }}>
                        {r.lap}
                      </span>
                      {r.pit && tag("PIT", PIT)}
                      {r.caution && tag("FCY", CAUTION, "#0b0b18")}
                    </span>
                  </td>
                  <td className="py-2.5 text-right font-mono" style={{ color: r.best ? GREEN : PC.text, fontWeight: r.best ? 700 : 400 }}>
                    {lapClock(r.time)}
                  </td>
                  <td className="py-2.5 pr-1 text-right font-mono" style={{ color: PC.muted }}>
                    {lapClock(r.avg)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="m-0 py-8 text-center text-[14px]" style={{ color: PC.muted }}>
            No lap times for this car yet.
          </p>
        )}
        {rows.length > 0 && (
          <p className="m-0 mt-3 text-[12px] leading-relaxed" style={{ color: PC.muted }}>
            Best lap in green. Race avg: the car's average lap up to that lap, pit and caution laps included. Tap a lap to pick it on the charts.
          </p>
        )}
      </div>
    </div>
  );
}
